import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArchive, faClipboardList, faBuilding, faUserShield,
  faDatabase, faChevronRight, faChevronDown, faArrowLeft,
  faSearch, faPlus, faTrash, faEdit, faUpload, faSave,
  faUser, faEnvelope, faPhone, faCalendarAlt, faMapMarkerAlt,
  faLock, faKey, faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';

const AdminTuto = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const sections = [
    {
      id: 'archivage',
      icon: faArchive,
      title: '1.1 Archivage Numérique',
      color: '#3b82f6',
      steps: [
        {
          step: 'a',
          title: 'Accès à la section',
          description: 'Dans le menu latéral gauche, cliquez sur "Archivage numérique" pour accéder à la gestion des documents archivés.',
          tip: '💡 Astuce : Cette section permet de centraliser tous vos documents administratifs et légaux.'
        },
        {
          step: 'b',
          title: 'Ajout d\'un document',
          description: 'Cliquez sur le bouton "Ajouter" pour ouvrir un formulaire modal contenant les champs suivants :',
          details: [
            '📄 Nom du document (ex: Contrat de travail 2024)',
            '📂 Type (ex: Contrat, Facture, Rapport, Attestation)',
            '🏷️ Catégorie (ex: RH, Comptabilité, Légal)',
            '📎 Upload du fichier (PDF, DOC, JPG, PNG)',
            '📅 Date du document'
          ],
          tip: '💡 Astuce : Utilisez des noms de fichiers explicites pour faciliter la recherche ultérieure.'
        },
        {
          step: 'c',
          title: 'Recherche de documents',
          description: 'Utilisez la barre de recherche en haut de la liste pour filtrer les documents par nom.',
          tip: '💡 Astuce : La recherche est instantanée et s\'effectue en temps réel.'
        }
      ]
    },
    {
      id: 'criteres',
      icon: faClipboardList,
      title: '1.2 Gestion des Critères d\'Évaluations',
      color: '#10b981',
      steps: [
        {
          step: 'a',
          title: 'Accès à la section',
          description: 'Cliquez sur "Critères d\'évaluation" dans le menu latéral pour accéder à la gestion des grilles d\'évaluation.',
          tip: '💡 Astuce : Les critères d\'évaluation sont utilisés pour les entretiens annuels et les évaluations de performance.'
        },
        {
          step: 'b',
          title: 'Ajout d\'un critère',
          description: 'Cliquez sur "Ajouter" pour ouvrir un formulaire modal avec les champs :',
          details: [
            '📝 Libellé (ex: Esprit d\'équipe)',
            '📋 Description détaillée du critère',
            '🔢 Ordre d\'affichage (numéro de priorité)'
          ],
          tip: '💡 Astuce : Définissez un ordre logique pour faciliter la lecture des évaluations.'
        },
        {
          step: 'c',
          title: 'Recherche de critères',
          description: 'Utilisez la barre de recherche pour trouver un critère par son libellé ou sa description.',
          tip: '💡 Astuce : La recherche est insensible à la casse.'
        },
        {
          step: 'd',
          title: 'Suppression d\'un critère',
          description: 'Cliquez sur le bouton rouge avec l\'icône poubelle pour supprimer définitivement le critère.',
          warning: '⚠️ Attention : Cette action est irréversible et supprime également toutes les évaluations associées.'
        },
        {
          step: 'e',
          title: 'Modification d\'un critère',
          description: 'Cliquez sur le bouton gris avec l\'icône stylo pour modifier le critère sélectionné.',
          tip: '💡 Astuce : Les modifications sont automatiquement sauvegardées et affectent toutes les évaluations futures.'
        }
      ]
    },
    {
      id: 'entreprise',
      icon: faBuilding,
      title: '1.3 Informations de l\'Entreprise',
      color: '#f59e0b',
      steps: [
        {
          step: 'a',
          title: 'Accès à la section',
          description: 'Cliquez sur "Entreprise Infos" dans le menu latéral pour accéder aux informations légales de votre entreprise.',
          tip: '💡 Astuce : Ces informations sont utilisées automatiquement dans les documents officiels (contrats, factures, etc.).'
        },
        {
          step: 'b',
          title: 'Formulaire d\'information',
          description: 'Un formulaire complet s\'affiche avec les champs suivants à remplir :',
          details: [
            '🏢 Nom de l\'entreprise',
            '🖼️ URL du logo (image au format PNG ou JPG)',
            '✍️ URL de la signature (image du cachet)',
            '📍 Adresse complète',
            '📞 Téléphone de contact',
            '📧 Email officiel',
            '🏦 Compte bancaire (IBAN)',
            '📋 RCCM (Registre du Commerce)',
            '🆔 ID National',
            '🧾 N° d\'impôt (NIF)'
          ],
          tip: '💡 Astuce : Assurez-vous que le logo et la signature sont clairs pour les documents officiels.'
        }
      ]
    },
    {
      id: 'admin',
      icon: faUserShield,
      title: '1.4 Gestion des Administrateurs',
      color: '#8b5cf6',
      steps: [
        {
          step: 'a',
          title: 'Accès à la section',
          description: 'Cliquez sur "Administrateurs" dans le menu latéral pour gérer les utilisateurs ayant des droits d\'administration.',
          tip: '💡 Astuce : Seuls les Super Admins ont accès à cette section.'
        },
        {
          step: 'b',
          title: 'Ajout d\'un administrateur',
          description: 'Cliquez sur "Ajouter" pour ouvrir un formulaire modal avec les champs suivants :',
          details: [
            '👤 Nom et Prénom',
            '📧 Email (unique)',
            '📱 Téléphone',
            '📅 Date de naissance',
            '⚥ Sexe (Masculin/Féminin)',
            '💼 Fonction (ex: DSI, RH, Comptable)',
            '📍 Adresse',
            '🔵 Statut (Actif ou Inactif)',
            '🔑 Mot de passe (minimum 8 caractères)',
            '🎯 Attribution des rôles et permissions'
          ],
          tip: '💡 Astuce : Les rôles déterminent les accès et fonctionnalités disponibles pour chaque administrateur.'
        }
      ]
    },
    {
      id: 'reset',
      icon: faDatabase,
      title: '1.5 Réinitialisation des Données',
      color: '#ef4444',
      steps: [
        {
          step: 'a',
          title: 'Accès à la section',
          description: 'Cliquez sur "Réinitialisation" dans le menu latéral pour accéder aux outils de maintenance des données.',
          warning: '⚠️ Attention : Cette section est réservée aux Super Admins et DSI. Une mauvaise manipulation peut entraîner une perte de données.'
        },
        {
          step: 'b',
          title: 'Procédure de réinitialisation',
          description: 'Cliquez sur le bouton "Vider" pour ouvrir un modal de confirmation avec :',
          details: [
            '🔐 Saisie du mot de passe administrateur',
            '📋 Liste des avertissements sur les conséquences',
            '☑️ Cases à cocher pour confirmer la compréhension',
            '⚠️ Bouton de confirmation en rouge'
          ],
          tip: '💡 Astuce : Effectuez toujours une sauvegarde complète avant toute opération de réinitialisation.'
        }
      ]
    }
  ];

  return (
    <>
      <style>{`
        /* ============================================================
           STYLES – ADMIN TUTO BISENTER ERP
           ============================================================ */
        :root {
          --bg-gradient: radial-gradient(circle at 10% 20%, #0f172a, #020617);
          --card-bg: rgba(255,255,255,0.04);
          --card-border: rgba(255,255,255,0.06);
          --text-primary: #e2e8f0;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --accent-blue: #3b82f6;
          --accent-green: #10b981;
          --accent-amber: #f59e0b;
          --accent-purple: #8b5cf6;
          --accent-red: #ef4444;
          --radius: 20px;
          --shadow: 0 20px 60px rgba(0,0,0,0.6);
        }

        .tuto-container {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: 2rem 1.5rem;
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          line-height: 1.6;
        }

        .tuto-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 2rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid rgba(255,255,255,0.04);
        }

        .tuto-header .back-link {
          color: var(--text-secondary);
          text-decoration: none;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border-radius: 30px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          transition: 0.2s;
          font-size: 0.9rem;
        }
        .tuto-header .back-link:hover {
          background: rgba(255,255,255,0.08);
          color: #fff;
        }

        .tuto-header h1 {
          font-size: clamp(1.8rem, 4vw, 2.8rem);
          font-weight: 700;
          background: linear-gradient(135deg, #60a5fa, #06b6d4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin: 0;
        }

        .tuto-header .subtitle {
          color: var(--text-secondary);
          font-size: clamp(0.9rem, 1.2vw, 1.1rem);
          margin-top: 0.2rem;
        }

        .tuto-intro {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.04);
          border-radius: var(--radius);
          padding: 1.5rem 2rem;
          margin-bottom: 2rem;
        }
        .tuto-intro .badge-roles {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-top: 0.5rem;
        }
        .tuto-intro .badge-roles span {
          padding: 0.25rem 0.8rem;
          border-radius: 30px;
          font-size: 0.75rem;
          font-weight: 500;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          color: var(--text-secondary);
        }
        .tuto-intro .badge-roles .admin { background: rgba(244,63,94,0.15); color: #f43f5e; border-color: rgba(244,63,94,0.15); }
        .tuto-intro .badge-roles .dsi { background: rgba(6,182,212,0.15); color: #06b6d4; border-color: rgba(6,182,212,0.15); }

        .section-card {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.04);
          border-radius: var(--radius);
          margin-bottom: 1.2rem;
          overflow: hidden;
          transition: 0.3s;
        }
        .section-card:hover {
          border-color: rgba(255,255,255,0.08);
        }

        .section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.2rem 1.8rem;
          cursor: pointer;
          transition: 0.2s;
          background: rgba(255,255,255,0.01);
          user-select: none;
        }
        .section-header:hover {
          background: rgba(255,255,255,0.03);
        }
        .section-header .left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .section-header .left .icon {
          font-size: 1.5rem;
          width: 36px;
          text-align: center;
        }
        .section-header .left .title {
          font-size: clamp(1.1rem, 1.4vw, 1.3rem);
          font-weight: 600;
        }
        .section-header .left .badge {
          font-size: 0.65rem;
          padding: 0.15rem 0.6rem;
          border-radius: 30px;
          background: rgba(255,255,255,0.04);
          color: var(--text-muted);
        }
        .section-header .toggle-icon {
          color: var(--text-muted);
          transition: transform 0.3s;
          font-size: 0.9rem;
        }
        .section-header .toggle-icon.open {
          transform: rotate(180deg);
        }

        .section-content {
          padding: 0 1.8rem 1.8rem 1.8rem;
          border-top: 1px solid rgba(255,255,255,0.03);
          animation: slideDown 0.3s ease;
        }

        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .step-item {
          display: flex;
          gap: 1rem;
          padding: 0.8rem 0;
          border-bottom: 1px solid rgba(255,255,255,0.02);
        }
        .step-item:last-child {
          border-bottom: none;
        }
        .step-item .step-number {
          font-weight: 700;
          font-size: 1.1rem;
          min-width: 28px;
          color: var(--text-muted);
        }
        .step-item .step-content {
          flex: 1;
        }
        .step-item .step-content .step-title {
          font-weight: 600;
          color: #fff;
          margin-bottom: 0.2rem;
        }
        .step-item .step-content .step-desc {
          color: var(--text-secondary);
          font-size: 0.95rem;
        }
        .step-item .step-content .step-details {
          margin-top: 0.4rem;
          padding-left: 1.2rem;
        }
        .step-item .step-content .step-details li {
          color: var(--text-secondary);
          font-size: 0.9rem;
          list-style: none;
          padding: 0.1rem 0;
        }
        .step-item .step-content .step-details li::before {
          content: '• ';
          color: var(--accent-blue);
        }
        .step-item .step-content .step-tip {
          margin-top: 0.4rem;
          padding: 0.4rem 1rem;
          background: rgba(59,130,246,0.08);
          border-left: 3px solid var(--accent-blue);
          border-radius: 6px;
          font-size: 0.9rem;
          color: var(--text-secondary);
        }
        .step-item .step-content .step-warning {
          margin-top: 0.4rem;
          padding: 0.4rem 1rem;
          background: rgba(239,68,68,0.08);
          border-left: 3px solid var(--accent-red);
          border-radius: 6px;
          font-size: 0.9rem;
          color: #fca5a5;
        }

        .footer-tuto {
          text-align: center;
          padding-top: 2rem;
          border-top: 1px solid rgba(255,255,255,0.04);
          color: var(--text-secondary);
          font-size: 0.85rem;
        }

        @media (max-width: 768px) {
          .tuto-container { padding: 1.2rem; }
          .tuto-header { flex-direction: column; align-items: flex-start; }
          .section-header { padding: 1rem 1.2rem; flex-wrap: wrap; gap: 0.5rem; }
          .section-content { padding: 0 1.2rem 1.2rem 1.2rem; }
          .step-item { flex-direction: column; gap: 0.3rem; }
          .tuto-intro { padding: 1rem; }
        }
      `}</style>

      <div className="tuto-container">
        {/* ===== EN-TÊTE ===== */}
        <div className="tuto-header">
          <a href="/dashboard" className="back-link">
            <FontAwesomeIcon icon={faArrowLeft} /> Retour
          </a>
          <div>
            <h1>📘 Tutoriel Administration</h1>
            <div className="subtitle">Bisenter ERP – Guide complet pour les administrateurs</div>
          </div>
        </div>

        {/* ===== INTRODUCTION ===== */}
        <div className="tuto-intro">
          <p style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
            Ce tutoriel couvre l'ensemble des fonctionnalités du module <strong style={{ color: '#fff' }}>Administration</strong> 
            de l'application Bisenter ERP. Il est destiné aux <strong style={{ color: '#fff' }}>Super Administrateurs</strong> 
            et aux <strong style={{ color: '#fff' }}>DSI</strong>.
          </p>
          <div className="badge-roles">
            <span className="admin"><FontAwesomeIcon icon={faUserShield} /> Super Administrateur</span>
            <span className="dsi"><FontAwesomeIcon icon={faUser} /> DSI – Directeur du Système Informatique</span>
          </div>
        </div>

        {/* ===== SECTIONS ===== */}
        {sections.map((section) => (
          <div key={section.id} className="section-card">
            <div className="section-header" onClick={() => toggleSection(section.id)}>
              <div className="left">
                <span className="icon" style={{ color: section.color }}>
                  <FontAwesomeIcon icon={section.icon} />
                </span>
                <span className="title">{section.title}</span>
                <span className="badge">{section.steps.length} étapes</span>
              </div>
              <FontAwesomeIcon
                icon={expandedSection === section.id ? faChevronDown : faChevronRight}
                className={`toggle-icon ${expandedSection === section.id ? 'open' : ''}`}
              />
            </div>

            {expandedSection === section.id && (
              <div className="section-content">
                {section.steps.map((step, index) => (
                  <div key={index} className="step-item">
                    <div className="step-number">{step.step}.</div>
                    <div className="step-content">
                      <div className="step-title">{step.title}</div>
                      <div className="step-desc">{step.description}</div>
                      {step.details && (
                        <ul className="step-details">
                          {step.details.map((detail, idx) => (
                            <li key={idx}>{detail}</li>
                          ))}
                        </ul>
                      )}
                      {step.tip && (
                        <div className="step-tip">
                          <FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '0.5rem', color: 'var(--accent-blue)' }} />
                          {step.tip}
                        </div>
                      )}
                      {step.warning && (
                        <div className="step-warning">
                          <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '0.5rem', color: 'var(--accent-red)' }} />
                          {step.warning}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* ===== PIED DE PAGE ===== */}
        <div className="footer-tuto">
          <p>
            <strong>Besoin d'aide ?</strong> Contactez le support technique à <span style={{ color: 'var(--accent-blue)' }}>support@bisenter.com</span>
          </p>
          <p style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            © {new Date().getFullYear()} Bisenter – Tous droits réservés.
          </p>
        </div>
      </div>
    </>
  );
};

// Icône Lightbulb (ajoutée localement car non importée ci-dessus)
const faLightbulb = {
  prefix: 'fas',
  iconName: 'lightbulb',
  icon: [576, 512, [], 'f0eb', 'M96 64c0-35.3 28.7-64 64-64h256c35.3 0 64 28.7 64 64v256c0 35.3-28.7 64-64 64H160c-35.3 0-64-28.7-64-64V64z']
};

export default AdminTuto;